import { Button, Dropdown, Input, Menu, Tooltip } from '@arco-design/web-react';
import { IconPhone, IconSearch, IconUser } from '@arco-design/web-react/icon';
import IconBlacklistShield from '../../../components/icons/IconBlacklistShield';
import { maskPhone } from '../../../utils/format';

export interface QuickDialIdlePaneProps {
  /** 外部话机坐席（agent.phone_mode=desk）：号码框置灰 + 只读说明 */
  deskPhone: boolean;
  /** 软电话状态（仅 error 时常显「注册失败」重注册出口） */
  status: string;
  number: string;
  numberRef: React.RefObject<any>;
  history: string[];
  histLoading: boolean;
  histQuery: string;
  /** 最近拨号按关键字收敛（入口侧已算好：号码原串或脱敏串命中） */
  filteredHistory: string[];
  onHistQueryChange: (v: string) => void;
  /** 号码框输入：入口侧负责解除回显映射 + 过滤非法字符（只留 0-9*#） */
  onNumberInput: (v: string) => void;
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** 呼出（号码框回车 / 绿色呼出按钮） */
  onCall: () => void;
  /** 历史点选：脱敏回显到号码框 + 暂存真实号供呼出还原 */
  onEchoNumber: (n: string) => void;
  /** 展开「最近拨号」时重拉本人最近呼出记录 */
  onLoadHistory: () => void;
  /** 注册失败一键重新注册（复用自愈逻辑） */
  onHeal: () => void;
  /** 话后态：顶部常驻「加入黑名单 / 预约回呼」入口 */
  inAfterCall: boolean;
  onOpenBlacklist: () => void;
  onOpenCallback: () => void;
}

/**
 * 顶栏胶囊的「闲时」内容：号码输入 + 呼出 + 最近拨号下拉 + 话后「加入黑名单 / 预约回呼」。
 * 数据与写操作全部由入口侧传入（本分片不持有状态、不直连 store）。
 */
export default function QuickDialIdlePane({
  deskPhone,
  status,
  number,
  numberRef,
  history,
  histLoading,
  histQuery,
  filteredHistory,
  onHistQueryChange,
  onNumberInput,
  onInputKeyDown,
  onCall,
  onEchoNumber,
  onLoadHistory,
  onHeal,
  inAfterCall,
  onOpenBlacklist,
  onOpenCallback,
}: QuickDialIdlePaneProps) {
  return (
    <>
      {/* 注册态可见：接听/外呼都依赖 FS 注册，而失败以前只有一次 3 秒 toast，之后界面无痕迹——
          坐席看不出"系统在线但电话打不出去"。这里只在**注册失败**（status=error）时常显并提供
          一键重新注册（复用自愈逻辑）；idle/registering 属过程态（页面刷新即 idle→registering→
          registered），一闪而过会让坐席误以为电话不可用，故不展示。
          外部话机坐席不注册软电话，改显只读说明。 */}
      {deskPhone ? (
        <Tooltip content="当前坐席使用外部话机接听：来电在话机上振铃，接听/挂断/转接请在话机上操作">
          <Button size="mini" type="text" disabled>
            外部话机
          </Button>
        </Tooltip>
      ) : (
        status === 'error' && (
          <Tooltip content="软电话注册失败，点击重新注册">
            <Button size="mini" type="text" status="danger" onClick={() => onHeal()}>
              注册失败
            </Button>
          </Tooltip>
        )
      )}
      <Input
        ref={numberRef}
        className="bcc-quick-dial-input"
        size="small"
        placeholder={deskPhone ? '外部话机模式，请在话机上拨号' : '请输入号码'}
        value={number}
        allowClear
        disabled={deskPhone}
        onChange={(v) => onNumberInput(String(v || ''))}
        onKeyDown={onInputKeyDown}
        onPressEnter={onCall}
        title="输入号码回车呼叫"
      />
      <Button
        className="bcc-quick-dial-call"
        size="small"
        type="primary"
        shape="circle"
        icon={<IconPhone />}
        onClick={onCall}
        disabled={!number.trim() || deskPhone}
        title={deskPhone ? '外部话机模式，请在话机上拨号' : '呼出'}
        aria-label="呼出"
      />
      <Dropdown
        droplist={
          <div className="bcc-quick-dial-hist">
            <Input
              size="small"
              value={histQuery}
              placeholder="搜索号码"
              prefix={<IconSearch style={{ fontSize: 13 }} />}
              onChange={(v) => onHistQueryChange(String(v || ''))}
              allowClear
            />
            <Menu
              className="bcc-quick-dial-hist-list"
              onClickMenuItem={(key) => {
                if (key === '__empty') return;
                // 行即动作：点任意一行即回拨（号码脱敏回显，真实号暂存供呼出还原）
                onEchoNumber(key);
              }}
              style={{ minWidth: 0 }}
            >
              {filteredHistory.length === 0 ? (
                <Menu.Item key="__empty" disabled>
                  {histLoading ? '加载中…' : history.length === 0 && !histQuery ? '暂无拨号历史' : '无匹配号码'}
                </Menu.Item>
              ) : (
                filteredHistory.map((n) => (
                  <Menu.Item key={n}>
                    <span className="bcc-quick-dial-hist-num">{maskPhone(n)}</span>
                  </Menu.Item>
                ))
              )}
            </Menu>
          </div>
        }
        onVisibleChange={(v) => {
          if (v) onLoadHistory();
          else onHistQueryChange('');
        }}
        trigger="click"
      >
        <Button
          className="bcc-quick-dial-history"
          size="small"
          shape="circle"
          icon={<IconUser />}
          title="最近拨号"
          aria-label="最近拨号"
        />
      </Dropdown>
      {/* 话后处理：上一通结束后的常用动作常驻顶部，直到新电话振铃切换 */}
      {inAfterCall && (
        <Button
          size="small"
          type="outline"
          status="warning"
          icon={<IconBlacklistShield className="arco-icon" />}
          aria-label="加入黑名单"
          title="将上一通号码加入黑名单"
          onClick={onOpenBlacklist}
        >
          加入黑名单
        </Button>
      )}
      {inAfterCall && (
        <Button size="small" type="outline" aria-label="预约回呼" title="预约到点回呼上一通号码" onClick={onOpenCallback}>
          预约回呼
        </Button>
      )}
    </>
  );
}
